import * as Sentry from '@sentry/react';
import {
  COOKIE_CONSENT_EVENT_NAME,
  getStoredCookieConsent,
  isAnalyticsEnabledByConsent,
  type CookieConsentValue,
} from '@/lib/cookie-consent';

const SENTRY_DSN_KEY = 'VITE_SENTRY_DSN';
const SENTRY_TRACES_SAMPLE_RATE_KEY = 'VITE_SENTRY_TRACES_SAMPLE_RATE';
const SENTRY_ENV_KEY = 'VITE_SENTRY_ENVIRONMENT';
const DEFAULT_SENTRY_ENV = 'production';

type ImportMetaEnv = { env?: Record<string, unknown> };

function readEnvVar(key: string): unknown {
  const env = (import.meta as ImportMetaEnv).env;
  /* istanbul ignore next */
  return env ? env[key] : undefined;
}

let initialized = false;

interface InitSentryOptions {
  /** Override cookie consent check (used in tests). */
  hasAnalyticsConsent?: boolean;
  /** Override production check (used in tests). */
  isProduction?: boolean;
  /** Override DSN value (used in tests). */
  dsn?: string | undefined;
  /** Override traces sample rate (used in tests). */
  tracesSampleRate?: number | undefined;
  /** Override environment (used in tests). */
  environment?: string | undefined;
  /** Override import.meta.env reading (used in tests). */
  readEnv?: () => { isProd: boolean; dsn: string | undefined };
  /** Override import.meta.env reading for additional keys (used in tests). */
  readEnvVars?: (key: string) => unknown;
}

interface RegisterSentryConsentListenerOptions {
  initOptions?: InitSentryOptions;
}

interface ResolvedInitOptions {
  hasAnalyticsConsent: boolean;
  isProduction: boolean;
  dsn: string | undefined;
  tracesSampleRate: number | undefined;
  environment: string | undefined;
}

function readDefaultEnv(): { isProd: boolean; dsn: string | undefined } {
  /* istanbul ignore next */
  const env = (import.meta as ImportMetaEnv).env ?? {};
  /* istanbul ignore next */
  const isProd = env.PROD === true || env.MODE === 'production';
  const rawDsn = env[SENTRY_DSN_KEY];
  /* istanbul ignore next */
  const dsn = typeof rawDsn === 'string' && rawDsn.length > 0 ? rawDsn : undefined;
  return { isProd, dsn };
}

function parseSampleRate(raw: unknown): number | undefined {
  if (typeof raw !== 'string' || raw.length === 0) {
    return undefined;
  }
  const numeric = Number(raw);
  if (!Number.isFinite(numeric) || numeric < 0 || numeric > 1) {
    return undefined;
  }
  return numeric;
}

export function resolveInitOptions(options: InitSentryOptions = {}): ResolvedInitOptions {
  const envDefaults = options.readEnv ? options.readEnv() : readDefaultEnv();
  const readVar = options.readEnvVars ?? readEnvVar;
  const envTracesSampleRate = parseSampleRate(readVar(SENTRY_TRACES_SAMPLE_RATE_KEY));
  const rawEnvironment = readVar(SENTRY_ENV_KEY);
  const envEnvironment = typeof rawEnvironment === 'string' ? rawEnvironment : undefined;

  const hasAnalyticsConsent =
    options.hasAnalyticsConsent ?? isAnalyticsEnabledByConsent(getStoredCookieConsent());

  return {
    hasAnalyticsConsent,
    isProduction: options.isProduction ?? envDefaults.isProd,
    dsn: options.dsn ?? envDefaults.dsn,
    tracesSampleRate: options.tracesSampleRate ?? envTracesSampleRate,
    environment: options.environment ?? envEnvironment,
  };
}

/**
 * Initialize Sentry for the browser.
 *
 * No-op unless ALL of these conditions hold:
 *   1. import.meta.env.PROD === true (production build only)
 *   2. VITE_SENTRY_DSN is configured
 *   3. Cookie consent grants analytics (`getStoredCookieConsent() === 'all'`)
 *
 * Safe to call on every page load; subsequent calls are no-ops.
 */
export function initSentry(options: InitSentryOptions = {}): boolean {
  if (initialized) {
    return true;
  }

  const resolved = resolveInitOptions(options);

  if (!resolved.isProduction) {
    return false;
  }

  if (!resolved.dsn) {
    return false;
  }

  if (!resolved.hasAnalyticsConsent) {
    return false;
  }

  Sentry.init({
    dsn: resolved.dsn,
    environment: resolved.environment ?? DEFAULT_SENTRY_ENV,
    tracesSampleRate: resolved.tracesSampleRate ?? 0.05,
    integrations: [],
    sendDefaultPii: false,
  });
  initialized = true;
  return true;
}

export function registerSentryConsentListener(
  options: RegisterSentryConsentListenerOptions = {}
): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const handleConsentUpdate = (event: Event) => {
    if ((event as CustomEvent<CookieConsentValue>).detail === 'all') {
      initSentry(options.initOptions);
    }
  };

  window.addEventListener(COOKIE_CONSENT_EVENT_NAME, handleConsentUpdate);
  return () => window.removeEventListener(COOKIE_CONSENT_EVENT_NAME, handleConsentUpdate);
}

export function isSentryInitialized(): boolean {
  return initialized;
}

/** Test helper: reset internal state between test runs. */
export function __resetSentryForTests(): void {
  initialized = false;
}

export { Sentry };
export type { InitSentryOptions, RegisterSentryConsentListenerOptions, ResolvedInitOptions };

export function captureSentryException(error: unknown, context?: Record<string, unknown>): void {
  if (!initialized) {
    return;
  }
  if (context && Object.keys(context).length > 0) {
    Sentry.withScope((scope) => {
      scope.setContext('boundary', context);
      Sentry.captureException(error);
    });
    return;
  }
  Sentry.captureException(error);
}

export function captureSentryMessage(message: string, context?: Record<string, unknown>): void {
  if (!initialized) {
    return;
  }
  if (context && Object.keys(context).length > 0) {
    Sentry.withScope((scope) => {
      scope.setContext('frontend', context);
      Sentry.captureMessage(message);
    });
    return;
  }
  Sentry.captureMessage(message);
}
